import { useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FALLBACK_MS = 1500;

/**
 * Haalt de bewerkbare inhoud van een pagina op uit de database.
 *
 * Geeft null terug zolang de API niet geantwoord heeft (maximaal FALLBACK_MS,
 * daarna vallen we terug op de meegegeven code-teksten, ook als de API faalt).
 * Zo ziet een bezoeker nooit kort een oude tekst: we wachten op de database,
 * tenzij die er niet is.
 *
 * Gebruik:
 *   const c = usePageContent("home", DEFAULTS);
 *   if (!c) return <Laadscherm />;
 *   c.t("home.hero.subtitle")      -> tekst
 *   c.list("home.marquee.items")   -> array
 *   c.img("home.portrait.foto")    -> { src, alt }
 */
export function usePageContent(page, defaults) {
    const [blocks, setBlocks] = useState(null);

    useEffect(() => {
        let alive = true;
        const timer = setTimeout(() => {
            if (alive) setBlocks({});
        }, FALLBACK_MS);
        axios
            .get(`${API}/content`, { params: { page } })
            .then((r) => {
                if (alive) {
                    clearTimeout(timer);
                    setBlocks(r.data?.blocks || {});
                }
            })
            .catch(() => {
                if (alive) {
                    clearTimeout(timer);
                    setBlocks({});
                }
            });
        return () => {
            alive = false;
            clearTimeout(timer);
        };
    }, [page]);

    return useMemo(() => {
        if (blocks === null) return null;
        const t = (key) =>
            blocks[key] && blocks[key].value != null ? blocks[key].value : defaults[key];
        const list = (key) =>
            Array.isArray(blocks[key]?.value) ? blocks[key].value : defaults[key];
        const img = (key) => ({
            src: blocks[key]?.value || defaults[key]?.src,
            alt: blocks[key]?.alt ?? defaults[key]?.alt,
        });
        return { t, list, img };
    }, [blocks, defaults]);
}

/** Leeg, rustig laadscherm in de huisstijl (geen flits van tekst). */
export function ContentLoading() {
    return (
        <div
            data-testid="content-loading"
            className="min-h-[100svh] bg-[var(--hds-sand)]"
        />
    );
}
